import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "../components/layout";
import { Button, Card, StatusIndicator } from "../components/ui";
import { ContextBar } from "../components/context-bar";
import { CountChip, KeyVal, formatAge, shortHash } from "../components/phase3";
import { ConfirmGate, SafeError, approvalTone, classificationTone, deploymentTone } from "../components/phase4";
import { EmptyState, NotConfiguredState } from "../components/states";
import { useAppContext } from "../hooks/useAppContext";
import {
  api,
  apiConfigured,
  ApiError,
  type DeploymentView,
  type RollbackAction,
  type RollbackApprovalView,
  type RollbackPlan,
  type RollbackPlanView,
} from "../lib/api";

/** The explicit acknowledgement a rollback approver must accept. */
export const ROLLBACK_CONFIRMATION_TEXT =
  "A rollback is a NEW controlled deployment, not an undo button. It restores supported properties from the immutable " +
  "pre-deployment snapshot and may DELETE resources ODIN provably created during this deployment. Rollback is NOT " +
  "guaranteed to fully restore the prior state, and the live server is re-checked before anything is applied.";

function isRestore(a: RollbackAction): boolean {
  return a.actionType.startsWith("restore-");
}
function isDelete(a: RollbackAction): boolean {
  return a.actionType.startsWith("delete-");
}

// ── Presentational (testable) ────────────────────────────────────────────────
export function RollbackReviewView({
  deploymentStatus,
  plan,
  approval,
  now = Date.now(),
  busy = false,
  actionError = null,
  onApprove,
  onQueue,
}: {
  deploymentStatus: DeploymentView["status"];
  plan: RollbackPlan;
  approval: RollbackApprovalView | null;
  now?: number;
  busy?: boolean;
  actionError?: string | null;
  onApprove?: () => void;
  onQueue?: () => void;
}): JSX.Element {
  const restores = useMemo(() => plan.actions.filter(isRestore), [plan.actions]);
  const deletes = useMemo(() => plan.actions.filter(isDelete), [plan.actions]);
  const manualReview = useMemo(() => plan.actions.filter((a) => a.classification === "MANUAL_REVIEW"), [plan.actions]);
  const blocked = plan.classification === "BLOCKED";
  const approved = approval?.status === "APPROVED";

  return (
    <div className="rollback-review">
      {/* Non-negotiable: rollback is not guaranteed. */}
      <div className="report-banner report-banner--warn" role="status">
        <strong>Rollback is NOT guaranteed.</strong>
        <span>{ROLLBACK_CONFIRMATION_TEXT}</span>
      </div>

      <Card title="Why a rollback is available">
        <p>
          This deployment is <StatusIndicator tone={deploymentTone(deploymentStatus)} label={deploymentStatus} />. ODIN
          offers a rollback because it has an immutable pre-deployment snapshot plus an action journal proving which
          resources it created or changed — the only safe basis for a controlled reverse step.
        </p>
      </Card>

      <Card title="Safety classification & scope">
        <div className="kv-grid">
          <KeyVal
            k="Classification"
            v={<StatusIndicator tone={classificationTone(plan.classification)} label={plan.classification} />}
          />
          <KeyVal k="Total actions" v={plan.actions.length} />
        </div>
        <div className="row" style={{ marginTop: "0.75rem" }}>
          <CountChip label="restore" value={restores.length} tone="neutral" />
          <CountChip label="remove" value={deletes.length} tone={deletes.length ? "danger" : "neutral"} />
          <CountChip label="manual review" value={manualReview.length} tone={manualReview.length ? "warn" : "neutral"} />
          <CountChip label="blockers" value={plan.blockers.length} tone={plan.blockers.length ? "danger" : "neutral"} />
        </div>
        {blocked ? (
          <p className="dryrun-blocked" style={{ marginTop: "0.75rem" }}>
            This rollback plan is <strong>BLOCKED</strong> (it would touch protected or unsafe resources) and cannot be
            approved or queued.
          </p>
        ) : null}
      </Card>

      <Card title="Current-server freshness">
        <p className="section-note">
          The plan compares the immutable pre-deployment snapshot against the latest captured snapshot of your server.
          Before executing, the worker re-reads the live server; any resource a user has since changed is escalated to
          manual review rather than force-restored.
        </p>
      </Card>

      <Card title={`Resources to restore (${restores.length})`}>
        {restores.length === 0 ? (
          <p className="muted">Nothing needs restoring.</p>
        ) : (
          <div className="op-list">
            {restores.map((a, i) => (
              <ActionRow key={`${a.resourceKey}-${i}`} action={a} />
            ))}
          </div>
        )}
      </Card>

      <Card title={`Resources proposed for removal (${deletes.length})`}>
        {deletes.length === 0 ? (
          <p className="muted">No resources are proposed for removal.</p>
        ) : (
          <>
            <p className="section-note">
              Removal is proposed ONLY for resources ODIN created during this deployment. Protected resources are never
              deleted.
            </p>
            <div className="op-list">
              {deletes.map((a, i) => (
                <ActionRow key={`${a.resourceKey}-${i}`} action={a} />
              ))}
            </div>
          </>
        )}
      </Card>

      <Card title={`Manual-review findings (${manualReview.length + plan.blockers.length + plan.warnings.length})`}>
        {manualReview.length === 0 && plan.blockers.length === 0 && plan.warnings.length === 0 ? (
          <p className="muted">No manual-review findings.</p>
        ) : (
          <>
            {plan.blockers.length > 0 ? (
              <ul className="dryrun-blocked">
                {plan.blockers.map((b, i) => (
                  <li key={`b-${i}`}>{b}</li>
                ))}
              </ul>
            ) : null}
            {plan.warnings.length > 0 ? (
              <ul className="dryrun-stale">
                {plan.warnings.map((w, i) => (
                  <li key={`w-${i}`}>{w}</li>
                ))}
              </ul>
            ) : null}
            {manualReview.map((a, i) => (
              <ActionRow key={`mr-${a.resourceKey}-${i}`} action={a} />
            ))}
          </>
        )}
      </Card>

      {/* ── Decision ─────────────────────────────────────────────────────── */}
      <section>
        <h2 className="section-title">Decision</h2>
        {actionError ? <SafeError message={actionError} /> : null}

        <Card title="Approval">
          {approval ? (
            <div className="kv-grid">
              <KeyVal k="Rollback approval" v={<StatusIndicator tone={approvalTone(approval.status)} label={approval.status} />} />
              <KeyVal k="Approver" v={approval.approverUserId} mono />
              <KeyVal k="Expires" v={formatAge(approval.expiresAt, now)} />
              <KeyVal k="Bound to plan" v={shortHash(approval.planHash)} mono />
            </div>
          ) : (
            <p className="muted">This rollback plan has not been approved yet.</p>
          )}
        </Card>

        {!blocked && !approved ? (
          <Card title="Approve this rollback">
            <ConfirmGate
              terms={
                <ul>
                  <li>You are authorizing a REAL, controlled rollback deployment against your live server.</li>
                  <li>Resources ODIN created during this deployment may be deleted.</li>
                  <li>Rollback restores supported properties only and is NOT guaranteed to fully restore prior state.</li>
                  <li>This approval is bound to this exact rollback plan; a changed plan requires a new approval.</li>
                </ul>
              }
              acknowledgeLabel="I have read the terms and I authorize this controlled rollback."
              confirmLabel="Approve rollback"
              busy={busy}
              onConfirm={() => onApprove?.()}
            />
          </Card>
        ) : null}

        {!blocked && approved ? (
          <Card title="Queue the rollback" description="Queueing revalidates the approval and hands the rollback to the worker, which performs the authoritative live recheck.">
            <Button variant="primary" disabled={busy} onClick={() => onQueue?.()}>
              {busy ? "Working…" : "Queue rollback"}
            </Button>
          </Card>
        ) : null}
      </section>
    </div>
  );
}

function ActionRow({ action }: { action: RollbackAction }): JSX.Element {
  return (
    <div className="op-row">
      <div className="op-row__head">
        <span className="op-row__action">{action.actionType}</span>
        <code className="op-row__key">{action.resourceKey}</code>
        <StatusIndicator tone={classificationTone(action.classification)} label={action.classification} />
        {action.destructive ? <span className="marker marker--destructive">destructive</span> : null}
      </div>
      <p className="op-row__reason">{action.reason}</p>
    </div>
  );
}

// ── Data-fetching container ──────────────────────────────────────────────────
export function RollbackReview(): JSX.Element {
  const ctx = useAppContext();
  const [deploymentId, setDeploymentId] = useState("");
  const [deployment, setDeployment] = useState<DeploymentView | null>(null);
  const [plan, setPlan] = useState<RollbackPlanView | null>(null);
  const [approval, setApproval] = useState<RollbackApprovalView | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const generate = async () => {
    if (!ctx.tenantId || deploymentId.trim().length < 1) return;
    setBusy(true);
    setError(null);
    setActionError(null);
    setPlan(null);
    setApproval(null);
    const id = deploymentId.trim();
    try {
      const dep = await api.getDeployment(ctx.tenantId, id);
      setDeployment(dep);
      const p = await api.rollbackPlan(ctx.tenantId, id);
      setPlan(p);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not build a rollback plan");
    } finally {
      setBusy(false);
    }
  };

  const approve = async () => {
    if (!ctx.tenantId || !deployment) return;
    setBusy(true);
    setActionError(null);
    try {
      setApproval(await api.rollbackApprove(ctx.tenantId, deployment.id));
    } catch (e) {
      setActionError(e instanceof ApiError ? e.message : "Could not approve the rollback");
    } finally {
      setBusy(false);
    }
  };

  const queue = async () => {
    if (!ctx.tenantId || !deployment) return;
    setBusy(true);
    setActionError(null);
    try {
      setDeployment(await api.rollbackQueue(ctx.tenantId, deployment.id));
    } catch (e) {
      setActionError(e instanceof ApiError ? e.message : "Could not queue the rollback");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <PageHeader title="Rollback review">
        <p>
          Plan, review and approve a controlled rollback. Rollback is a separate deployment step and is not guaranteed —
          see the outcome afterwards on the <Link to="/app/release-report">release report</Link>.
        </p>
      </PageHeader>

      {!apiConfigured ? (
        <NotConfiguredState title="Control API URL not configured">
          Set <code>VITE_CONTROL_API_URL</code> to review rollbacks.
        </NotConfiguredState>
      ) : (
        <>
          <ContextBar />
          {!ctx.tenantId ? (
            <EmptyState title="Select a workspace">Choose a workspace above, then plan a rollback by deployment id.</EmptyState>
          ) : (
            <>
              <Card title="Plan a rollback" description="Build a rollback plan from a deployment's immutable pre-deploy snapshot and action journal.">
                <div className="row">
                  <input
                    className="field__input"
                    placeholder="Deployment id"
                    value={deploymentId}
                    onChange={(e) => setDeploymentId(e.target.value)}
                    aria-label="Deployment id"
                  />
                  <Button variant="secondary" disabled={busy || deploymentId.trim().length < 1} onClick={generate}>
                    {busy ? "Planning…" : "Plan rollback"}
                  </Button>
                </div>
              </Card>

              {error ? <SafeError message={error} /> : null}
              {plan && deployment ? (
                <RollbackReviewView
                  deploymentStatus={deployment.status}
                  plan={plan.content}
                  approval={approval}
                  busy={busy}
                  actionError={actionError}
                  onApprove={approve}
                  onQueue={queue}
                />
              ) : null}
            </>
          )}
        </>
      )}
    </>
  );
}
